// src/domains/dash.tgbot.ts —— 06 C-15 Telegram Bot 配置页签 1「当前 Bot 配置」原生 renderVals（经 domains/dash.ts 分派器挂载；页签 0 列表见 pages/tgbot）。
// enter（进入 tgbot 页）：拉 /api/public/config（limits.min_deposit 供 minTopup 本地校验、app_url 供预览 Mini App 回落，TD-910）；
// 已选中 Bot → GET /bots/{id}；未选中 → 取最早创建的平台 Bot（原型 curBot 默认平台官方 Bot）。列表行点击经 selectBot 切换。
// 编辑稿（draft）、脏标记、在途锁与红描边集合存模块私有 WeakMap<host, TgRun>；reset 换代即丢弃（会话边界 AS10b：下一账号看不到上一账号的 Bot 配置）。
// 下发 POST /bots/{id}/publish 前本地拦截两项（06 C-15）：启用键 label ∪ label_en 重复（errKbDup，行红描边）、minTopup < 系统 minDeposit（errMinTopup）——命中即不发请求。
import { ApiError } from '../../../shared/src/api/client';
import type { AdminPage, PublicConfig } from '../../../shared/src/api/types';
import type { Dict } from '../../../shared/src/dc';
import { interpolate } from '../../../shared/src/errors';
import { cmp, fromMicro, parseDecimalInput, toMicro } from '../../../shared/src/money';
import type { BotCommand, BotConfig, BotDetail, BotKey, BotRow, PublishStep } from '../pages/tgbot.types';
import { gate } from '../perm';
import type { DashPart } from './dash';
import { openConfirm, sessionGuard } from './table';
import { str, type AdminCtx, type InputEvent } from './types';

const EMPTY = '—';
const noop = (): void => undefined;

// ---- 编辑稿 ----

interface CmdRow extends BotCommand {
  uid: number;
}

interface KeyRow extends BotKey {
  uid: number;
}

// 文本输入一律按字符串持有，下发时再转数字（金额 / TTL 先过本地格式校验）
export interface Draft {
  botTitle: string;
  botUsername: string;
  support: string;
  updates: string;
  miniUrl: string;
  menuBtnType: string;
  miniAppOn: boolean;
  miniLabel: string;
  miniLabelEn: string;
  cmds: readonly CmdRow[];
  keys: readonly KeyRow[];
  welcome: string;
  welcomeEn: string;
  agentText: string;
  agentTextEn: string;
  amounts: string;
  minTopup: string;
  maxTopup: string;
  orderTtl: string;
}

type TextKey = { [K in keyof Draft]: Draft[K] extends string ? K : never }[keyof Draft];

interface TgRun {
  sel: number | null; // 当前编辑的 Bot 记录 id
  detail: BotDetail | null;
  draft: Draft | null;
  dirty: boolean;
  bad: Set<string>; // 红描边字段（Draft 键名）
  dup: Set<number>; // 键盘文案重复的行 uid
  minDeposit: string | null; // limits.min_deposit 十进制串；未取到 = 只靠服务端 1102
  appUrl: string;
  locks: Set<string>; // publish / test / hook / regen
  ticket: number; // 详情请求序号：后发先至时丢弃旧响应
  uid: number;
}

const runs = new WeakMap<object, TgRun>();

function fresh(sel: number | null = null): TgRun {
  return { sel, detail: null, draft: null, dirty: false, bad: new Set(), dup: new Set(), minDeposit: null, appUrl: '', locks: new Set(), ticket: 0, uid: 0 };
}

function runOf(host: object): TgRun {
  let r = runs.get(host);
  if (!r) {
    r = fresh();
    runs.set(host, r);
  }
  return r;
}

function bump(ctx: AdminCtx): void {
  const ds = ctx.host.state.ds;
  ctx.host.setState({ ds: { rev: ds.rev + 1 } });
}

// 异步回调里的文案以实时字典为准（分派器传入的 ctx.t 为挂载时字典，语言可能已切换）
function dictOf(ctx: AdminCtx): Dict {
  return ctx.gp.ui.dict();
}

function amountText(v: number): string {
  return Number.isFinite(v) ? String(v) : '';
}

function draftOf(run: TgRun, d: BotDetail): Draft {
  const c = d.config;
  const cmds = [...(c.commands ?? [])].sort((a, b) => a.order - b.order).map((x) => ({ ...x, desc_en: x.desc_en ?? '', uid: ++run.uid }));
  const keys = [...(c.keyboard ?? [])].sort((a, b) => a.order - b.order).map((x) => ({ ...x, label_en: x.label_en ?? '', uid: ++run.uid }));
  return {
    botTitle: c.botTitle ?? '',
    botUsername: d.username,
    support: c.support ?? '',
    updates: d.allowed_updates,
    miniUrl: c.miniUrl ?? '',
    menuBtnType: c.menuBtnType || 'web_app',
    miniAppOn: c.miniAppOn !== false,
    miniLabel: c.miniLabel ?? '',
    miniLabelEn: c.miniLabel_en ?? '',
    cmds,
    keys,
    welcome: c.welcome ?? '',
    welcomeEn: c.welcome_en ?? '',
    agentText: c.agentText ?? '',
    agentTextEn: c.agentText_en ?? '',
    amounts: Array.isArray(c.amounts) ? c.amounts.map(amountText).join(',') : '',
    minTopup: amountText(c.minTopup),
    maxTopup: amountText(c.maxTopup),
    orderTtl: amountText(c.orderTtl),
  };
}

// 本地编辑：换稿 → 置脏 → 清该字段红描边 → 重渲染
function edit(ctx: AdminCtx, f: (d: Draft) => Draft, clear: readonly string[] = []): void {
  const run = runOf(ctx.host);
  if (!run.draft) return;
  run.draft = f(run.draft);
  run.dirty = true;
  for (const k of clear) run.bad.delete(k);
  bump(ctx);
}

function setText(ctx: AdminCtx, k: TextKey, v: string): void {
  edit(ctx, (d) => ({ ...d, [k]: v }), [k]);
}

// 按 order 排序后与相邻行交换 order（原型 moveBot 口径）
function move<T extends { uid: number; order: number }>(list: readonly T[], uid: number, dir: -1 | 1): readonly T[] {
  const sorted = [...list].sort((a, b) => a.order - b.order);
  const i = sorted.findIndex((x) => x.uid === uid);
  const j = i + dir;
  const a = sorted[i];
  const b = sorted[j];
  if (i < 0 || !a || !b) return list;
  sorted[i] = { ...a, order: b.order };
  sorted[j] = { ...b, order: a.order };
  return sorted;
}

const sortByOrder = <T extends { order: number }>(list: readonly T[]): T[] => [...list].sort((a, b) => a.order - b.order);

// ---- 本地校验（06 C-15：label 重复 / minTopup ≥ minDeposit；金额 / TTL 格式）----

// 同一 Bot 内全部启用键的 label ∪ label_en 两两不重复；同一键 label 与 label_en 相同不算重复（botcfg.validateKeyboard 同口径，TD-908）
export function dupKeyRows(keys: readonly Pick<KeyRow, 'uid' | 'label' | 'label_en' | 'enabled'>[]): Set<number> {
  const owner = new Map<string, number>();
  const out = new Set<number>();
  for (const k of keys) {
    if (!k.enabled) continue;
    for (const raw of [k.label, k.label_en]) {
      const l = raw.trim();
      if (l === '') continue;
      const prev = owner.get(l);
      if (prev !== undefined && prev !== k.uid) {
        out.add(prev);
        out.add(k.uid);
      } else owner.set(l, k.uid);
    }
  }
  return out;
}

// 十进制串去尾零（"10.000000" → "10"）
function trimDec(s: string): string {
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
}

function decOf(raw: string): string | null {
  return parseDecimalInput(raw, 6);
}

interface LocalFail {
  bad: Set<string>;
  dup: Set<number>;
  msg: string;
}

function validateLocal(d: Draft, minDeposit: string | null, t: Dict): LocalFail | null {
  const bad = new Set<string>();
  const msgs: string[] = [];
  const dup = dupKeyRows(d.keys);
  if (dup.size > 0) msgs.push(str(t, 'errKbDup'));
  const field = (k: string, label: string): void => {
    bad.add(k);
    msgs.push(interpolate(str(t, 'errCfgField'), { field: label }));
  };
  const amounts = d.amounts.split(',').map((x) => x.trim());
  if (amounts.length === 0 || amounts.some((x) => decOf(x) === null)) field('amounts', str(t, 'tgAmounts'));
  const min = decOf(d.minTopup);
  if (min === null) field('minTopup', str(t, 'tgMin'));
  if (decOf(d.maxTopup) === null) field('maxTopup', str(t, 'tgMax'));
  if (!/^\d+$/.test(d.orderTtl.trim())) field('orderTtl', str(t, 'tgTtl'));
  if (min !== null && minDeposit !== null) {
    try {
      if (cmp(toMicro(min), toMicro(minDeposit)) < 0) {
        bad.add('minTopup');
        msgs.push(interpolate(str(t, 'errMinTopup'), { minDeposit: trimDec(fromMicro(toMicro(minDeposit))) }));
      }
    } catch (e) {
      console.warn('[gp:tgbot] minDeposit 非法，跳过本地校验', minDeposit, e);
    }
  }
  return dup.size > 0 || bad.size > 0 ? { bad, dup, msg: msgs[0] ?? '' } : null;
}

// 下发载荷（botcfg.Config 18 键，逐键显式构造：publish 严格解码，未知键 → 4220 field=config）；数字已过本地格式校验
function configOf(base: BotConfig, d: Draft): BotConfig {
  const num = (raw: string): number => Number(decOf(raw) ?? 'NaN');
  return {
    botTitle: d.botTitle,
    support: d.support.trim(),
    welcome: d.welcome,
    welcome_en: d.welcomeEn,
    agentText: d.agentText,
    agentText_en: d.agentTextEn,
    commands: sortByOrder(d.cmds).map((c, i) => ({ command: c.command, desc: c.desc, desc_en: c.desc_en, enabled: c.enabled, order: i + 1 })),
    keyboard: sortByOrder(d.keys).map((k, i) => ({ emoji: k.emoji, label: k.label, label_en: k.label_en, type: k.type, action: k.action.trim(), enabled: k.enabled, order: i + 1 })),
    menuBtnType: d.menuBtnType,
    miniUrl: d.miniUrl.trim(),
    miniAppOn: d.miniAppOn,
    miniLabel: d.miniLabel,
    miniLabel_en: d.miniLabelEn,
    parse_mode: base.parse_mode || 'HTML',
    amounts: d.amounts.split(',').map((x) => num(x.trim())),
    minTopup: num(d.minTopup),
    maxTopup: num(d.maxTopup),
    orderTtl: Number.parseInt(d.orderTtl.trim(), 10),
  };
}

// ---- API 请求预览（06 C-15 previewJson，实时联动）----

// Mini App URL 追加 ?bot={记录 id}（P-09，保留原查询串）；非法 URL 原样返回
function withBot(raw: string, id: number): string {
  try {
    const u = new URL(raw);
    u.searchParams.set('bot', String(id));
    return u.toString();
  } catch {
    return raw;
  }
}

function chunk<T>(arr: readonly T[], n: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

function updatesOf(raw: string): string[] {
  return raw.split(',').map((x) => x.trim()).filter((x) => x !== '');
}

// 默认列表 = desc_en（空回退 desc）、language_code=zh 列表 = desc（Q-901 / S-57）
export function previewOf(det: BotDetail, d: Draft, appUrl: string): string {
  const cmds = sortByOrder(d.cmds).filter((c) => c.enabled);
  const mini = d.miniUrl.trim() || appUrl;
  const miniOk = d.miniAppOn && mini !== '';
  const keys = sortByOrder(d.keys).filter((k) => k.enabled && (k.type !== 'web_app' || miniOk));
  const btn = (k: KeyRow): Record<string, unknown> => {
    const text = k.emoji ? `${k.emoji} ${k.label}` : k.label;
    return k.type === 'web_app' ? { text, web_app: { url: withBot(k.action, det.id) } } : { text };
  };
  const tgId = Number(det.bot_telegram_id);
  return JSON.stringify(
    {
      getMe: { id: Number.isSafeInteger(tgId) ? tgId : det.bot_telegram_id, is_bot: true, username: d.botUsername.replace(/^@/, ''), first_name: d.botTitle },
      setMyCommands: [
        { commands: cmds.map((c) => ({ command: c.command, description: c.desc_en.trim() || c.desc })), scope: { type: 'default' } },
        { commands: cmds.map((c) => ({ command: c.command, description: c.desc })), scope: { type: 'default' }, language_code: 'zh' },
      ],
      setChatMenuButton: d.menuBtnType === 'web_app' && miniOk ? { menu_button: { type: 'web_app', text: d.miniLabel, web_app: { url: withBot(mini, det.id) } } } : { menu_button: { type: 'commands' } },
      reply_markup: { keyboard: chunk(keys.map(btn), 2), resize_keyboard: true, is_persistent: true },
      setWebhook: det.mode === 'webhook' ? { url: det.webhook_url ?? EMPTY, secret_token: det.secret_masked || EMPTY, allowed_updates: updatesOf(d.updates) } : null,
    },
    null,
    2,
  );
}

// 无选中 Bot / 详情未到：结构同 06 previewJson，值为空
const EMPTY_PREVIEW = JSON.stringify(
  { getMe: null, setMyCommands: [{ commands: [], scope: { type: 'default' } }, { commands: [], scope: { type: 'default' }, language_code: 'zh' }], setChatMenuButton: { menu_button: { type: 'commands' } }, reply_markup: { keyboard: [], resize_keyboard: true, is_persistent: true }, setWebhook: null },
  null,
  2,
);

// ---- 加载 ----

function report(ctx: AdminCtx, run: TgRun, e: unknown): void {
  if (runs.get(ctx.host) === run) ctx.gp.ui.toast(ctx.gp.ui.err(e));
}

async function loadDetail(ctx: AdminCtx, run: TgRun, id: number): Promise<void> {
  const ticket = ++run.ticket;
  const d = await ctx.gp.api.call<BotDetail>('GET /api/admin/bots/{id}', { path: { id } });
  if (runs.get(ctx.host) !== run || run.ticket !== ticket || run.sel !== id) return;
  run.detail = d;
  run.draft = draftOf(run, d);
  run.dirty = false;
  run.bad = new Set();
  run.dup = new Set();
  bump(ctx);
}

function reload(ctx: AdminCtx, run: TgRun): void {
  if (run.sel !== null) void loadDetail(ctx, run, run.sel).catch((e: unknown) => report(ctx, run, e));
}

async function loadConfig(ctx: AdminCtx, run: TgRun): Promise<void> {
  const cfg = await ctx.gp.api.call<PublicConfig>('GET /api/public/config');
  if (runs.get(ctx.host) !== run) return;
  const md = cfg.limits['min_deposit'];
  run.minDeposit = typeof md === 'string' || typeof md === 'number' ? String(md) : null;
  run.appUrl = typeof cfg.app_url === 'string' ? cfg.app_url : '';
  bump(ctx);
}

// 未选中时默认编辑平台主 Bot（0039）；本页无主 Bot 则取最早创建的平台 Bot（启用优先）；期间用户已点选其他 Bot 则不覆盖
async function pickDefault(ctx: AdminCtx, run: TgRun): Promise<void> {
  const page = await ctx.gp.api.call<AdminPage<BotRow>>('GET /api/admin/bots', { query: { owner: 'platform', page: 1, size: 10, sort: 'created_at', order: 'asc' } });
  if (runs.get(ctx.host) !== run || run.sel !== null) return;
  const rows = Array.isArray(page.list) ? page.list : [];
  const pick = rows.find((r) => r.is_primary === true) ?? rows.find((r) => r.status === 'active') ?? rows[0];
  if (!pick) return;
  run.sel = pick.id;
  bump(ctx);
  await loadDetail(ctx, run, pick.id);
}

// 列表行点击 / 「编辑配置」：切到页签 1 并载入该 Bot（06 C-15，toast toastBotSwitched：@xxx）
// 换代（与 forgetBot 同一写法）：上一 Bot 的在途下发 / 测试 / 详情结果按 run 同一性丢弃，在途锁随旧 run 释放，不串写到新选中的 Bot
export function selectBot(ctx: AdminCtx, id: number, username: string): void {
  const prev = runOf(ctx.host);
  const run: TgRun = { ...fresh(id), minDeposit: prev.minDeposit, appUrl: prev.appUrl, uid: prev.uid };
  runs.set(ctx.host, run);
  ctx.host.setState({ tab: 1, ds: { rev: ctx.host.state.ds.rev + 1 } });
  ctx.gp.ui.toast(`${str(dictOf(ctx), 'toastBotSwitched')}：${username}`);
  void loadDetail(ctx, run, id).catch((e: unknown) => report(ctx, run, e));
}

// 列表「当前编辑」标记（06 C-15 Bot 名称列 sub）
export function selectedBotId(host: object): number | null {
  return runs.get(host)?.sel ?? null;
}

// 删除当前编辑的 Bot 后清空页签 1
export function forgetBot(host: object, id: number): void {
  const run = runs.get(host);
  if (run?.sel !== id) return;
  runs.set(host, { ...fresh(), minDeposit: run.minDeposit, appUrl: run.appUrl });
}

// ---- 动作 ----

function editGate(ctx: AdminCtx): string {
  const g = gate(ctx.host.state.sh.me, dictOf(ctx), 'tgbot', 'edit');
  return g.disabled ? g.title : '';
}

// 1503（HOOK_PUBLIC_BASE_URL 未配置，set-webhook / regenerate-secret 前置）→ tgbErrNoPublicBase，不落通用「功能已关闭」；
// 1012 data.reason → 页文案；无 reason（非 webhook 模式调 set-webhook / regenerate-secret）→ tgbErrNotWebhook
function reasonText(d: Dict, e: unknown): string {
  if (!(e instanceof ApiError)) return '';
  if (e.code === 1503) return str(d, 'tgbErrNoPublicBase');
  // Telegram 限速（1403 + retry_after）：明确告知等待秒数，避免误读为上游故障而连续重试
  if (e.code === 1403 && e.retryAfter !== null) return interpolate(str(d, 'tgbErrRateLimited'), { n: String(e.retryAfter) });
  if (e.code !== 1012) return '';
  if (e.data.reason === 'bot_disabled') return str(d, 'tgbErrBotDisabled');
  if (e.data.reason === 'last_platform_bot') return str(d, 'tgbErrLastPlatform');
  return e.data.reason ? '' : str(d, 'tgbErrNotWebhook');
}

function errText(ctx: AdminCtx, e: unknown): string {
  return reasonText(dictOf(ctx), e) || ctx.gp.ui.err(e);
}

// 宿主级在途锁：同一动作在途期间重复点击忽略；结果写入前按 run 同一性与会话守卫丢弃。
// say：run 仍当前 → 原样提示；已切换 Bot（换代）但会话未变 → 「{上一 Bot 用户名}：」前缀提示（配置可能已存库，结果不静默丢弃，也不冒充当前 Bot）；会话已失效 → 丢弃
type Say = (msg: string) => void;

async function locked(ctx: AdminCtx, key: string, fn: (run: TgRun, det: BotDetail, alive: () => boolean, say: Say) => Promise<void>): Promise<void> {
  const run = runOf(ctx.host);
  const det = run.detail;
  if (!det || run.locks.has(key)) return;
  const denied = editGate(ctx);
  if (denied) {
    ctx.gp.ui.toast(denied);
    return;
  }
  const guard = sessionGuard(ctx);
  const alive = (): boolean => guard() && runs.get(ctx.host) === run;
  const say: Say = (msg) => {
    if (alive()) ctx.gp.ui.toast(msg);
    else if (guard()) ctx.gp.ui.toast(`${det.username}：${msg}`);
  };
  run.locks.add(key);
  bump(ctx);
  try {
    await fn(run, det, alive, say);
  } catch (e) {
    say(errText(ctx, e));
  } finally {
    run.locks.delete(key);
    if (runs.get(ctx.host) === run) bump(ctx);
  }
}

function testMe(ctx: AdminCtx): Promise<void> {
  return locked(ctx, 'test', async (_run, det, _alive, say) => {
    const r = await ctx.gp.api.call<{ id: string; username: string; first_name: string }>('POST /api/admin/bots/{id}/test', { path: { id: det.id }, body: {} });
    say(interpolate(str(dictOf(ctx), 'toastTgMe'), { username: r.username }));
  });
}

function setHook(ctx: AdminCtx): Promise<void> {
  return locked(ctx, 'hook', async (_run, det, _alive, say) => {
    await ctx.gp.api.call('POST /api/admin/bots/{id}/set-webhook', { path: { id: det.id }, body: {} });
    say(str(dictOf(ctx), 'toastTgHook'));
  });
}

// secret_token「重新生成」：确认弹层 → POST regenerate-secret（S-67：先以新 secret setWebhook，成功才落库；失败旧 secret 继续有效，toast err1401）
function regenSecret(ctx: AdminCtx): void {
  const run = runOf(ctx.host);
  const det = run.detail;
  if (!det || det.mode !== 'webhook') return;
  const denied = editGate(ctx);
  if (denied) {
    ctx.gp.ui.toast(denied);
    return;
  }
  const t = ctx.t;
  openConfirm(ctx, {
    title: str(t, 'regenSecretTitle'),
    text: str(t, 'regenSecretText'),
    label: `${det.name} · ${det.username}`,
    run: (c) =>
      locked(c, 'regen', async (r, d, alive, say) => {
        await c.gp.api.call('POST /api/admin/bots/{id}/regenerate-secret', { path: { id: d.id }, body: {} });
        say(str(dictOf(c), 'toastSecretRegen'));
        if (alive()) reload(c, r);
      }),
  });
}

// 服务端 data.field → Draft 键（TD-846 红描边映射；commands / keyboard / config 无单字段落点，只 toast）
const FIELD_OF: Readonly<Record<string, string>> = {
  botTitle: 'botTitle', username: 'botUsername', allowed_updates: 'updates', miniUrl: 'miniUrl', miniLabel: 'miniLabel',
  minTopup: 'minTopup', maxTopup: 'maxTopup', amounts: 'amounts', orderTtl: 'orderTtl',
  welcome: 'welcome', welcome_en: 'welcomeEn', agentText: 'agentText', agentText_en: 'agentTextEn',
};

function isStep(v: unknown): v is PublishStep {
  return typeof v === 'object' && v !== null && typeof Reflect.get(v, 'method') === 'string' && typeof Reflect.get(v, 'ok') === 'boolean';
}

// publish 失败信封 data.steps（1401 / 1403）：逐步「方法(语言) ✓ / ✗ 原因」
export function stepsText(steps: unknown): string {
  if (!Array.isArray(steps)) return '';
  return steps
    .filter(isStep)
    .map((s) => {
      const name = s.lang ? `${s.method}(${s.lang})` : s.method;
      if (s.ok) return `${name} ✓`;
      const err = typeof s.error === 'string' && s.error !== '' ? ` ${s.error}` : '';
      return `${name} ✗${err}`;
    })
    .join(' / ');
}

// 已切换 Bot 后的下发失败：只出提示文案（错误 + data.steps 逐步结果），不写红描边 / 不重取
function failText(ctx: AdminCtx, e: unknown): string {
  const steps = e instanceof ApiError ? stepsText(e.data.steps) : '';
  return steps ? `${errText(ctx, e)} · ${interpolate(str(dictOf(ctx), 'tgbSteps'), { steps })}` : errText(ctx, e);
}

function publishFailed(ctx: AdminCtx, run: TgRun, e: unknown): void {
  const d = dictOf(ctx);
  if (!(e instanceof ApiError)) {
    ctx.gp.ui.toast(ctx.gp.ui.err(e));
    return;
  }
  if (e.code === 1102 && e.data.field === 'minTopup') {
    run.bad = new Set(['minTopup']);
    const md = run.minDeposit;
    ctx.gp.ui.toast(md !== null ? interpolate(str(d, 'errMinTopup'), { minDeposit: trimDec(md) }) : ctx.gp.ui.err(e));
    return;
  }
  if (e.code === 4220) {
    const k = FIELD_OF[e.data.field ?? ''];
    if (k) run.bad = new Set([k]);
    if (e.data.field === 'keyboard' && e.data.reason === 'dup') {
      run.dup = run.draft ? dupKeyRows(run.draft.keys) : new Set();
      ctx.gp.ui.toast(str(d, 'errKbDup'));
      return;
    }
    ctx.gp.ui.toast(ctx.gp.ui.err(e));
    return;
  }
  ctx.gp.ui.toast(failText(ctx, e));
  // 1401 / 1403 部分失败：配置已存库（TB B-02），重取详情使编辑稿与库内一致
  if (stepsText(e.data.steps)) reload(ctx, run);
}

// 下发到 Bot（busy + 脏标记）：本地拦截 → publish（config 18 键 + 变更的 username / allowed_updates）→ toast；allowed_updates 变更且 webhook 模式时提示再点 setWebhook
function saveBot(ctx: AdminCtx): Promise<void> {
  const pre = runOf(ctx.host);
  const draft = pre.draft;
  if (!pre.detail || !draft || pre.locks.has('publish')) return Promise.resolve();
  if (!editGate(ctx)) {
    const fail = validateLocal(draft, pre.minDeposit, dictOf(ctx));
    if (fail) {
      pre.bad = fail.bad;
      pre.dup = fail.dup;
      bump(ctx);
      ctx.gp.ui.toast(fail.msg);
      return Promise.resolve();
    }
  }
  return locked(ctx, 'publish', async (run, det, alive, say) => {
    const updatesChanged = updatesOf(draft.updates).join(',') !== updatesOf(det.allowed_updates).join(',');
    const body: { config: BotConfig; username?: string; allowed_updates?: string } = { config: configOf(det.config, draft) };
    if (draft.botUsername.trim() !== det.username) body.username = draft.botUsername.trim();
    if (updatesChanged) body.allowed_updates = updatesOf(draft.updates).join(',');
    run.bad = new Set();
    run.dup = new Set();
    try {
      await ctx.gp.api.call<{ steps: PublishStep[] }>('POST /api/admin/bots/{id}/publish', { path: { id: det.id }, body });
    } catch (e) {
      if (alive()) publishFailed(ctx, run, e);
      else say(failText(ctx, e));
      return;
    }
    const d = dictOf(ctx);
    say(updatesChanged && det.mode === 'webhook' ? str(d, 'tgbHookAgain') : str(d, 'toastTgSaved'));
    if (!alive()) return;
    run.dirty = false;
    reload(ctx, run);
  });
}

function addCmd(ctx: AdminCtx): void {
  const run = runOf(ctx.host);
  edit(ctx, (d) => ({ ...d, cmds: [...d.cmds, { command: 'new_cmd', desc: '', desc_en: '', enabled: true, order: d.cmds.length + 1, uid: ++run.uid }] }));
}

// 06 C-15：+ 新增键盘按钮默认 ▫ / 新按钮 / text / home
function addKey(ctx: AdminCtx): void {
  const run = runOf(ctx.host);
  edit(ctx, (d) => ({ ...d, keys: [...d.keys, { emoji: '▫', label: '新按钮', label_en: '', type: 'text', action: 'home', enabled: true, order: d.keys.length + 1, uid: ++run.uid }] }));
}

// ---- renderVals ----

const INPUT = 'height:42px;border:1px solid #dfe5e2;border-radius:10px;padding:0 12px;font-size:13.5px;outline:none;flex:1;min-width:0';
const RO = ';background:#f3f5f4;color:#6b7772';
const BAD = ';border-color:#d1434b';
const SIDE_BTN = 'height:32px;padding:0 12px;border:1px solid #e6ebe8;border-radius:9px;background:#fff;font-weight:700;font-size:12px;cursor:pointer;white-space:nowrap;flex:none';
const KEY_LABEL = 'width:130px;height:36px;border:1px solid #dfe5e2;border-radius:9px;padding:0 10px;font-size:13px;outline:none';

interface SideBtn {
  label: string;
  run: () => void;
  title?: string;
  disabled?: boolean;
}

interface FieldOpts {
  wide?: boolean;
  ro?: boolean;
  bad?: boolean;
  placeholder?: string;
  onChange?: (v: string) => void;
  btn?: SideBtn;
}

// 原型 L922 bf() 字段结构逐键 + 0204 dev:C-15 块追加键（ro / inputStyle / 侧按钮）
// 只读双保险：真正只读的字段不传 onChange；ro 且带 onChange 的只有 draft 为 null 的情形，edit() 首行即返回——两层都不可删
function botField(k: string, label: string, value: string, o: FieldOpts = {}) {
  const on = o.onChange;
  const b = o.btn;
  return {
    k, label, value, wide: !!o.wide, area: false, isArea: false, isInput: true, placeholder: o.placeholder ?? '',
    onChange: on && !o.ro ? (e: InputEvent) => on(e.target.value) : noop,
    wrapStyle: `display:flex;flex-direction:column;gap:6px;${o.wide ? 'grid-column:1/-1' : ''}`,
    ro: !!o.ro,
    inputStyle: `${INPUT}${o.ro ? RO : ''}${o.bad ? BAD : ''}`,
    btnLabel: b?.label ?? '',
    btnRun: b && !b.disabled ? b.run : noop,
    btnTitle: b?.title ?? '',
    btnStyle: b?.disabled ? `${SIDE_BTN};opacity:.5;cursor:not-allowed` : SIDE_BTN,
  };
}

// 文案模板 textarea（原型 bf(...,{area:true}) 结构逐键）
function areaField(k: string, label: string, value: string, onChange: ((v: string) => void) | null) {
  return {
    k, label, value, wide: true, area: true, isArea: true, isInput: false, placeholder: '',
    onChange: onChange ? (e: InputEvent) => onChange(e.target.value) : noop,
    wrapStyle: 'display:flex;flex-direction:column;gap:6px;grid-column:1/-1',
  };
}

const fmtInt = (n: number): string => (Number.isFinite(n) ? n.toLocaleString('en-US') : EMPTY);
const pct = (a: number, b: number, dp: number): string => (b > 0 && Number.isFinite(a) ? `${((a / b) * 100).toFixed(dp)}%` : EMPTY);

// 原型 L979–L980 菜单类型按钮样式串逐字（选中绿描边）
function menuStyle(on: boolean): string {
  return `flex:1;height:40px;border:1px solid ${on ? '#0d9a68' : '#dfe5e2'};border-radius:10px;background:${on ? '#e3f6ee' : '#fff'};color:${on ? '#0d9a68' : '#0f1a17'};font-weight:700;font-size:13px;cursor:pointer`;
}

// 原型启停 toggle 样式串逐字
function onStyle(on: boolean): string {
  return `height:30px;padding:0 10px;border:0;border-radius:8px;font-size:12px;font-weight:700;cursor:pointer;background:${on ? '#e3f6ee' : '#f1f4f2'};color:${on ? '#0d9a68' : '#8a958f'}`;
}

function cmdRowsOf(ctx: AdminCtx, d: Draft) {
  const t = ctx.t;
  const patch = (uid: number, p: Partial<CmdRow>): void => edit(ctx, (x) => ({ ...x, cmds: x.cmds.map((c) => (c.uid === uid ? { ...c, ...p } : c)) }));
  return sortByOrder(d.cmds).map((c) => ({
    cmd: c.command,
    desc: c.desc,
    descEn: c.desc_en,
    descEnPh: str(t, 'tgbDescEn'),
    // 06 C-15：命令输入过滤 [^a-z_]、≤32；描述 ≤256
    onCmd: (e: InputEvent) => patch(c.uid, { command: e.target.value.replace(/[^a-z_]/g, '').slice(0, 32) }),
    onDesc: (e: InputEvent) => patch(c.uid, { desc: e.target.value.slice(0, 256) }),
    onDescEn: (e: InputEvent) => patch(c.uid, { desc_en: e.target.value.slice(0, 256) }),
    up: () => edit(ctx, (x) => ({ ...x, cmds: move(x.cmds, c.uid, -1) })),
    down: () => edit(ctx, (x) => ({ ...x, cmds: move(x.cmds, c.uid, 1) })),
    toggle: () => patch(c.uid, { enabled: !c.enabled }),
    del: () => edit(ctx, (x) => ({ ...x, cmds: x.cmds.filter((y) => y.uid !== c.uid) })),
    onStyle: onStyle(c.enabled),
    onText: str(t, c.enabled ? 'stOn' : 'stOff'),
  }));
}

function keyRowsOf(ctx: AdminCtx, d: Draft, dup: ReadonlySet<number>) {
  const t = ctx.t;
  const patch = (uid: number, p: Partial<KeyRow>): void => {
    const run = runOf(ctx.host);
    run.dup = new Set();
    edit(ctx, (x) => ({ ...x, keys: x.keys.map((k) => (k.uid === uid ? { ...k, ...p } : k)) }));
  };
  return sortByOrder(d.keys).map((k) => ({
    icon: k.emoji,
    label: k.label,
    labelEn: k.label_en,
    type: k.type,
    action: k.action,
    labelStyle: dup.has(k.uid) ? `${KEY_LABEL}${BAD}` : KEY_LABEL,
    labelEnPh: str(t, 'tgbLabelEn'),
    onIcon: (e: InputEvent) => patch(k.uid, { emoji: e.target.value }),
    onLabel: (e: InputEvent) => patch(k.uid, { label: e.target.value }),
    onLabelEn: (e: InputEvent) => patch(k.uid, { label_en: e.target.value }),
    // X-31：持久键盘类型只有 text / web_app，其余值不接受
    onType: (e: InputEvent) => {
      const v = e.target.value;
      if (v === 'text' || v === 'web_app') patch(k.uid, { type: v });
    },
    onAction: (e: InputEvent) => patch(k.uid, { action: e.target.value }),
    up: () => edit(ctx, (x) => ({ ...x, keys: move(x.keys, k.uid, -1) })),
    down: () => edit(ctx, (x) => ({ ...x, keys: move(x.keys, k.uid, 1) })),
    toggle: () => patch(k.uid, { enabled: !k.enabled }),
    del: () => {
      runOf(ctx.host).dup = new Set();
      edit(ctx, (x) => ({ ...x, keys: x.keys.filter((y) => y.uid !== k.uid) }));
    },
    onStyle: onStyle(k.enabled),
    onText: str(t, k.enabled ? 'stOn' : 'stOff'),
  }));
}

function statsOf(t: Dict, det: BotDetail | null) {
  if (!det) return [{ label: str(t, 'tgBound'), value: EMPTY, sub: EMPTY }, { label: str(t, 'tgFromBot'), value: EMPTY, sub: EMPTY }, { label: str(t, 'tgBindRate'), value: EMPTY, sub: EMPTY }];
  const s = det.stats;
  return [
    { label: `${str(t, 'tgBound')} · ${det.username}`, value: fmtInt(s.notify_users), sub: `/ ${fmtInt(s.total_users)}` },
    { label: str(t, 'tgFromBot'), value: fmtInt(s.via_users), sub: pct(s.via_users, s.total_users, 0) },
    { label: str(t, 'tgBindRate'), value: pct(s.notify_users, s.total_users, 1), sub: str(t, 'tgBound') },
  ];
}

function vals(ctx: AdminCtx): Record<string, unknown> {
  const t = ctx.t;
  const run = runOf(ctx.host);
  const det = run.detail;
  const d = det ? run.draft : null;
  const bad = (k: string): boolean => run.bad.has(k);
  const txt = (k: TextKey) => (v: string): void => setText(ctx, k, v);
  const webhook = det?.mode === 'webhook';
  const hookUrl = webhook ? det?.webhook_url ?? '' : '';
  const editable = d !== null;
  const val = (k: TextKey): string => (d ? d[k] : '');
  return {
    botBasic: [
      botField('botTitle', str(t, 'tgTitle'), val('botTitle'), { wide: true, bad: bad('botTitle'), onChange: txt('botTitle'), ro: !editable }),
      botField('botId', str(t, 'cBotId'), det?.bot_telegram_id ?? '', { ro: true }),
      botField('botUsername', str(t, 'tgUsername'), val('botUsername'), { bad: bad('botUsername'), onChange: txt('botUsername'), ro: !editable }),
      botField('token', str(t, 'tgToken'), det?.token_masked ?? '', { ro: true }),
      botField('support', str(t, 'tgSupport'), val('support'), { onChange: txt('support'), ro: !editable, placeholder: '@GoodPayU_support' }),
      botField('parse', str(t, 'tgParse'), det ? 'HTML' : '', { ro: true }),
      botField('webhook', str(t, 'tgWebhook'), det ? hookUrl || EMPTY : '', {
        wide: true, ro: true,
        ...(hookUrl ? { btn: { label: str(t, 'tgbCopy'), run: () => ctx.gp.ui.copy(hookUrl) } } : {}),
      }),
      botField('secret', str(t, 'tgSecret'), det?.secret_masked ?? '', {
        ro: true,
        ...(det ? { btn: { label: str(t, 'tgbRegen'), run: () => regenSecret(ctx), disabled: !webhook || run.locks.has('regen'), title: webhook ? '' : str(t, 'tgbRegenPolling') } } : {}),
      }),
      botField('updates', str(t, 'tgUpdates'), val('updates'), { bad: bad('updates'), onChange: txt('updates'), ro: !editable, placeholder: 'message,callback_query,my_chat_member' }),
      botField('miniUrl', str(t, 'tgMiniUrl'), val('miniUrl'), { wide: true, bad: bad('miniUrl'), onChange: txt('miniUrl'), ro: !editable }),
    ],
    // 06 C-15 botTopup 四字段（默认收款地址 address 废弃不实现，审计 A-7）
    botTopup: [
      botField('amounts', str(t, 'tgAmounts'), val('amounts'), { wide: true, bad: bad('amounts'), onChange: txt('amounts'), ro: !editable }),
      botField('minTopup', str(t, 'tgMin'), val('minTopup'), { bad: bad('minTopup'), onChange: txt('minTopup'), ro: !editable }),
      botField('maxTopup', str(t, 'tgMax'), val('maxTopup'), { bad: bad('maxTopup'), onChange: txt('maxTopup'), ro: !editable }),
      botField('orderTtl', str(t, 'tgTtl'), val('orderTtl'), { bad: bad('orderTtl'), onChange: txt('orderTtl'), ro: !editable }),
    ],
    botTexts: [
      areaField('welcome', str(t, 'tgWelcome'), val('welcome'), editable ? txt('welcome') : null),
      areaField('welcomeEn', str(t, 'tgbWelcomeEn'), val('welcomeEn'), editable ? txt('welcomeEn') : null),
      areaField('agentText', str(t, 'tgAgentText'), val('agentText'), editable ? txt('agentText') : null),
      areaField('agentTextEn', str(t, 'tgbAgentTextEn'), val('agentTextEn'), editable ? txt('agentTextEn') : null),
    ],
    // 06 C-15 菜单按钮文案（S-57）：0204 dev:C-15 块
    miniLabels: [
      { label: str(t, 'cMiniLabel'), value: val('miniLabel'), placeholder: 'GoodPayU', onChange: editable ? (e: InputEvent) => setText(ctx, 'miniLabel', e.target.value.slice(0, 64)) : noop, inputStyle: `${INPUT}${bad('miniLabel') ? BAD : ''}` },
      { label: str(t, 'cMiniLabelEn'), value: val('miniLabelEn'), placeholder: 'GoodPayU', onChange: editable ? (e: InputEvent) => setText(ctx, 'miniLabelEn', e.target.value.slice(0, 64)) : noop, inputStyle: `${INPUT}${bad('miniLabel') ? BAD : ''}` },
    ],
    botDirty: run.dirty,
    busyTgSave: run.locks.has('publish'),
    cmdRows: d ? cmdRowsOf(ctx, d) : [],
    keyRows: d ? keyRowsOf(ctx, d, run.dup) : [],
    menuTypes: (
      [
        ['commands', 'tgCmdMode'],
        ['web_app', 'tgWebAppMode'],
      ] as const
    ).map(([k, l]) => ({ label: str(t, l), pick: editable ? () => edit(ctx, (x) => ({ ...x, menuBtnType: k })) : noop, style: menuStyle(d?.menuBtnType === k) })),
    miniToggle: editable ? () => edit(ctx, (x) => ({ ...x, miniAppOn: !x.miniAppOn })) : noop,
    // 原型 miniStyle / miniKnob 样式串逐字（开启绿底、圆钮右移）
    miniStyle: `width:52px;height:30px;border-radius:999px;border:0;cursor:pointer;position:relative;background:${d?.miniAppOn ? '#0d9a68' : '#cfd6d2'}`,
    miniKnob: `position:absolute;top:3px;left:${d?.miniAppOn ? '25px' : '3px'};width:24px;height:24px;border-radius:50%;background:#fff;transition:left .18s`,
    previewJson: det && d ? previewOf(det, d, run.appUrl) : EMPTY_PREVIEW,
    tgStats: statsOf(t, det),
    saveBot: () => void saveBot(ctx),
    testMe: () => void testMe(ctx),
    // 06 C-15：setWebhook 仅 webhook 模式显示（0204 dev:C-15 块以真值门控；未载入时按原型显示）
    setHook: det && !webhook ? false : () => void setHook(ctx),
    addCmd: editable ? () => addCmd(ctx) : noop,
    addKey: editable ? () => addKey(ctx) : noop,
    curBotName: det?.name ?? EMPTY,
    curBotIdText: det?.bot_telegram_id || EMPTY,
    curBotUser: det?.username ?? '',
    curBotOwner: det ? (det.owner_kind === 'agent' ? det.agent_name ?? str(t, 'ownerAgent') : str(t, 'ownerPlatform')) : EMPTY,
  };
}

export const DASH_TGBOT: DashPart = {
  vals,
  // 进入 tgbot 页：换代（保留选中 id），并发拉公共参数与详情 / 默认 Bot；两块各自成败
  enter: (ctx) => {
    const run = fresh(runs.get(ctx.host)?.sel ?? null);
    runs.set(ctx.host, run);
    void loadConfig(ctx, run).catch((e: unknown) => console.warn('[gp:tgbot] public config 读取失败', e));
    const job = run.sel !== null ? loadDetail(ctx, run, run.sel) : pickDefault(ctx, run);
    void job.catch((e: unknown) => report(ctx, run, e));
  },
  reset: (host) => {
    runs.set(host, fresh());
  },
};
